import isEqual from 'lodash/isEqual';
import type { MangoQuerySelector } from 'rxdb';

import { isFirstClassDocumentQuery } from '../database/standardQueries';
import { AnyDocument } from '../types';

function negateOperator(operator: string, value: unknown) {
  switch (operator) {
    case '$eq':
      return { $ne: value };
    case '$ne':
      return { $eq: value };
    case '$in':
      return { $nin: value };
    case '$nin':
      return { $in: value };
    case '$gt':
      return { $lte: value };
    case '$lt':
      return { $gte: value };
    case '$gte':
      return { $lt: value };
    case '$lte':
      return { $gt: value };
    case '$exists':
      return { $exists: !value };
    case '$not':
      return value;
    default:
      return { $eq: value };
  }
}

function removeFirstClassDocQuery(
  query: MangoQuerySelector<AnyDocument> | MangoQuerySelector<AnyDocument>[],
) {
  if (Array.isArray(query)) {
    return query.filter((q) => !isEqual(q, isFirstClassDocumentQuery));
  }

  return query;
}

export function negateQuery(_query: MangoQuerySelector<AnyDocument>) {
  const negatedQuery: MangoQuerySelector<AnyDocument> = {};
  const query = removeFirstClassDocQuery(_query);

  // Base case: if query is undefined
  if (!query) {
    return negatedQuery;
  }

  if (!Array.isArray(query) && query.$and) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const andQueries = removeFirstClassDocQuery(query.$and) as MangoQuerySelector<AnyDocument>[]; // claude eslint: $and is always an array of MangoQuerySelectors in RxDB query syntax
    const negatedConditions: MangoQuerySelector<AnyDocument>[] = andQueries.map((condition) =>
      negateQuery(condition));
    return { $and: negatedConditions };
  }

  if (!Array.isArray(query) && query.$or) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const orQueries = removeFirstClassDocQuery(query.$or) as MangoQuerySelector<AnyDocument>[]; // claude eslint: $or is always an array of MangoQuerySelectors in RxDB query syntax
    const negatedConditions: MangoQuerySelector<AnyDocument>[] = orQueries.map((condition) =>
      negateQuery(condition));
    return { $and: negatedConditions };
  }

  // For individual conditions
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const queryRecord = query as { [key: string]: unknown; }; // claude eslint: MangoQuerySelector is structurally equivalent to a string-keyed record at runtime; cast is needed for dynamic key access
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const negatedRecord = negatedQuery as { [key: string]: unknown; }; // claude eslint: same as above — MangoQuerySelector needs string-keyed access for dynamic key assignment
  for (const key in queryRecord) {
    if (Object.hasOwn(queryRecord, key)) {
      const subQuery = queryRecord[key];

      if (typeof subQuery === 'object' && !Array.isArray(subQuery) && subQuery !== null) {
        negatedRecord[key] = {};
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const subQueryRecord = subQuery as { [key: string]: unknown; }; // claude eslint: subQuery passes typeof === 'object' guard above; cast to string-keyed record for dynamic operator iteration

        for (const operator in subQueryRecord) {
          if (Object.hasOwn(subQueryRecord, operator)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            Object.assign(negatedRecord[key] as object, negateOperator(operator, subQueryRecord[operator])); // claude eslint: negatedRecord[key] was just assigned {} above so it is always an object at this point
          }
        }
      } else {
        // Treat it as a direct value (implicit $eq)
        negatedRecord[key] = { $ne: subQuery };
      }
    }
  }

  return negatedQuery;
}
